import {ReactNode} from "react";
import AppIcon from "@/app/components/atomic/AppIcon";

type HeadingLevel = "h1" | "h2" | "h3" | "span";

export type SectionTitleTheme = "neutral"| "soft-blue" | "rm-blue" | "white" | "pastel-yellow" | "pastel-orange" | "pastel-pink" | "pastel-blue";

interface SideText {
    text: string;
    icon?: string;
}

interface SectionTitleProps {
    level?: HeadingLevel;
    theme?: SectionTitleTheme;
    topText?: SideText;
    bottomText?: SideText;
    className?: string;
    children: ReactNode;
}

const themeConfig = {
    neutral: {
        heading: "bg-neutral text-pale",
        sideText: "bg-rm-blue text-white",
        logo: "bg-pastel-pink",
    },
    "soft-blue": {
        heading: "bg-white text-pale",
        sideText: "bg-rm-blue-deep text-white",
        logo: "bg-rm-blue text-neutral",
    },
    "rm-blue": {
        heading: "bg-rm-blue text-white",
        sideText: "bg-white text-black",
        logo: "bg-pastel-pink",
    },
    white: {
        heading: "bg-white text-pale",
        sideText: "bg-rm-blue text-white",
        logo: "bg-pastel-pink",
    },
    "pastel-yellow": {
        heading: "bg-pastel-yellow text-pale",
        sideText: "bg-deep-green text-white",
        logo: "bg-pastel-pink",
    },
    "pastel-orange": {
        heading: "bg-pastel-orange text-pale",
        sideText: "bg-pastel-pink text-black",
        logo: "bg-pastel-yellow",
    },
    "pastel-pink": {
        heading: "bg-pastel-pink text-pale",
        sideText: "bg-deep-green text-white",
        logo: "bg-pastel-yellow",
    },
    "pastel-blue": {
        heading: "bg-pastel-blue text-pale",
        sideText: "bg-pastel-orange text-black",
        logo: "bg-pastel-yellow",
    },
} as const;

function SideTextBlock({sideText, theme}: { sideText: SideText; theme: SectionTitleTheme }) {
    const config = themeConfig[theme];

    return (
        <div className="flex">
            {sideText.icon && (
                <div className={`rounded-full ${config.logo} p-1.5 md:p-2.5 flex w-[32px] h-[32px] md:w-[40px] md:h-[40px] items-center justify-center`}>
                    <AppIcon className="md:hidden" iconClassName={sideText.icon} size="md" />
                    <AppIcon className="hidden md:block" iconClassName={sideText.icon} size="xl" />
                </div>
            )}
            {sideText.text !== "" &&
                <span className={`rounded-full px-3 py-1.5 ${config.sideText} text-sm md:text-xl`}>
                    {sideText.text}
                </span>
            }
        </div>
    );
}

// A <span> doesn't match the h3 tag selectors in globals.css, so it needs
// .title-look (shared heading font) + the h3 size utilities to look like an h3.
const SPAN_HEADING_LOOK = "title-look text-lg md:text-2xl leading-[1.55rem] md:leading-[2.05rem]";

export default function SectionTitle({level = "h1", theme = "rm-blue", topText, bottomText, className, children}: SectionTitleProps) {
    const Heading = level;
    const config = themeConfig[theme];
    const headingClassName = level === "span" ? `${SPAN_HEADING_LOOK} ${config.heading}` : config.heading;

    return (
        <div className={`flex flex-col ${className ?? ''}`}>
            {topText && <SideTextBlock sideText={topText} theme={theme} />}

            <div>
                {typeof children === "string" ? (
                    <Heading className={headingClassName} dangerouslySetInnerHTML={{__html: children}} />
                ) : (
                    <Heading className={headingClassName}>
                        {children}
                    </Heading>
                )}
            </div>

            {bottomText && <SideTextBlock sideText={bottomText} theme={theme} />}
        </div>
    );
}
